import { isNumeric } from '80.quickConnect.Core/helpers/common';
import { parseNumber } from '80.quickConnect.Core/helpers/parseNumber';
import { DateTimeFieldType, FieldType, OperatorResult } from '90.quickConnect.Models/enums';
import {
  BaseQCOperator,
  Dependency,
  AllFieldValueTypes,
  CtrlRefOperator,
  Choice,
  FieldDesc,
  QCNotification,
  AttachmentItemData,
  NotificationTarget,
  QCActionData,
  QCActionValueData,
} from '90.quickConnect.Models/models';

function isFloat(n: string | number) {
  return Number(n) === n && n % 1 !== 0;
}

function isInt(n: string | number) {
  return Number(n) === n && n % 1 === 0;
}

const getRefValue = (
  baseQCOperator: BaseQCOperator,
  dependencies: Dependency[],
): [AllFieldValueTypes, OperatorResult] => {
  const ctrlRef = baseQCOperator as CtrlRefOperator;
  let field = dependencies.find((d) => d.id.toUpperCase() === ctrlRef.controlId.toUpperCase());
  if (!field) field = dependencies.find((d) => d.fullPathId.toUpperCase() === ctrlRef.controlId.toUpperCase());
  if (!field) return [undefined, OperatorResult.ERROR];
  const { value, fieldType, type, label } = field as FieldDesc;
  switch (fieldType) {
    case FieldType.Combo:
    case FieldType.HierarchicalList:
    case FieldType.RadioList:
    case FieldType.CheckBoxList:
      return [value, OperatorResult.LISTCHOICE];
    case FieldType.Numeric:
      return [value, OperatorResult.DOUBLE];
    case FieldType.Digits:
      return [value, OperatorResult.INT];
    case FieldType.DateTime:
      return type === DateTimeFieldType.Date || !type ? [value, OperatorResult.DATE] : [value, OperatorResult.DATETIME];
    case FieldType.Notification:
      if (value) {
        const { selectedTargets } = value as QCNotification;
        return [selectedTargets.map(({ target }: NotificationTarget) => target).join(';'), OperatorResult.STRING];
      }
      return ['', OperatorResult.STRING];
    case FieldType.Time:
      return [value, OperatorResult.TIME];
    case FieldType.CheckBox:
      return [value, OperatorResult.BOOLEAN];
    case FieldType.Draw:
    case FieldType.Signature:
    case FieldType.Photo:
    case FieldType.Attachment:
    case FieldType.FixedImage:
    case FieldType.FixedAttachment:
      if (!Array.isArray(value)) return [false, OperatorResult.BOOLEAN];
      return [!!(value as AttachmentItemData[]).length, OperatorResult.BOOLEAN];
    case FieldType.Action:
      if (Array.isArray(value) && value.length > 0) {
        const actionData = value as QCActionData;
        const values = actionData.map((item: QCActionValueData) => {
          if (Array.isArray(item.value)) {
            // If value is an array of Choices, join their values
            return item.value.map((choice) => choice.value).join(', ');
          } else {
            // If value is a single Choice
            return item.value.value;
          }
        });
        return [values.join('; '), OperatorResult.STRING];
      }
      return [undefined, OperatorResult.ERROR];
    case FieldType.CodeReader:
    case FieldType.RfidReader:
    case FieldType.Text:
      // Treat fields with FieldType.Text always as STRING to avoid unintended coercion
      return [value ?? '', OperatorResult.STRING];
    case FieldType.Label:
      return [label ?? '', OperatorResult.STRING];
    case FieldType.Compute: {
      if (isNumeric(value)) {
        const n = parseNumber(value);
        if (Number.isNaN(n)) return [undefined, OperatorResult.ERROR];
        return [n, Number.isInteger(n) ? OperatorResult.INT : OperatorResult.DOUBLE];
      }
      return [value ?? '', OperatorResult.STRING];
    }
    default:
      if (isNumeric(value)) {
        const numericValue = parseNumber(value);
        if (Number.isNaN(numericValue)) return [undefined, OperatorResult.ERROR];
        const typeValue: OperatorResult = Number.isInteger(numericValue) ? OperatorResult.INT : OperatorResult.DOUBLE;
        return [numericValue, typeValue];
      }

      if (value === null || value === undefined) return [undefined, OperatorResult.ERROR];
      return [value, OperatorResult.STRING];
  }
};

export default getRefValue;
